import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';
import useAuthedChat from '../chatView/hooks/useAuthedChat';
import useSessionUrlSync from '../chatView/hooks/useSessionUrlSync';
import {
    clearPendingPrompt,
    takePendingPrompt,
    PENDING_PROMPT_EVENT,
} from '../topbar/components/askSingularAI/askSingularAIHandoff';
import ChatConversation from '../chatView/components/ChatConversation';
import SessionList from '../chatView/components/SessionList';
import StreamMonitor from '../chatView/components/StreamMonitor';
import SessionNoticeToasts from '../chatView/components/SessionNoticeToasts';
import ChatConnections from '../chatConnections/ChatConnections';
import ConnectionsProvider from '../chatConnections/ConnectionsProvider';
import SlackAgentProvider from '../slackAgent/SlackAgentProvider';
import Icon, { IconSizes, IconVariants } from '../components/foundations/Icon';
import { CHAT_EVENTS, CONTEXT_PROD_MODE, SESSION_TYPES, SUBMIT_STATUS, trackChatEvent } from '../chatView/constants';
import { getAdminModeEnabled } from '../selectors/user';
import ContextEnvSelect from '../chatView/components/ContextEnvSelect';
import SubPageHeader from '../components/molecules/SubPageHeader';
import css from './page.css';
import { Button } from '../components/widgets';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT';

const AnalysisChat = () => {
    const [showSessionSidebar, setShowSessionSidebar] = useState(true);
    const userControlsSidebarRef = useRef(false);
    const [contextEnv, setContextEnv] = useState(CONTEXT_PROD_MODE);
    const [showSessionDebug, setShowSessionDebug] = useState(false);

    const isAdminMode = useSelector(getAdminModeEnabled);

    const { chat, authLoading, authError } = useAuthedChat({
        runPath: 'runs',
        sessionType: SESSION_TYPES.CHAT,
        contextEnv: isAdminMode ? contextEnv : CONTEXT_PROD_MODE,
        adminMode: isAdminMode,
    });

    const {
        messages,
        currentSessionId,
        sessions,
        isLoadingSessions,
        sessionsLoadError,
        loadingSessionId,
        clearCurrentSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
        fetchSessions,
        streamDescriptors,
        streamingSessionIds,
        updatedSessionIds,
        handleStreamMessage,
        handleStreamError,
    } = chat;

    const [pendingAutoRun, setPendingAutoRun] = useState(null);

    const { urlSessionId, setSessionParam } = useSessionUrlSync({
        ready: !authLoading && !authError,
        openSessionId: currentSessionId,
        onOpenFromUrl: loadSession,
        onUrlCleared: clearCurrentSession,
        pauseStateSync: pendingAutoRun != null,
    });

    const readyRef = useRef(false);
    readyRef.current = !authLoading && !authError;
    const autoRunInFlightRef = useRef(false);

    const queuePendingPrompt = useCallback(() => {
        if (!readyRef.current) return;
        const pending = takePendingPrompt();
        if (pending) setPendingAutoRun(pending);
    }, []);

    useEffect(() => {
        queuePendingPrompt();
    }, [authLoading, authError, queuePendingPrompt]);

    useEffect(() => {
        window.addEventListener(PENDING_PROMPT_EVENT, queuePendingPrompt);
        return () => window.removeEventListener(PENDING_PROMPT_EVENT, queuePendingPrompt);
    }, [queuePendingPrompt]);

    useEffect(() => () => clearPendingPrompt(), []);

    useEffect(() => {
        if (pendingAutoRun == null || autoRunInFlightRef.current) return;
        if (loadingSessionId) return;
        if (currentSessionId) {
            setSessionParam(null);
            clearCurrentSession();
            return;
        }

        autoRunInFlightRef.current = true;
        clearCurrentSession();
        chat.handleSubmit(null, { content: pendingAutoRun.prompt })
            .then(status => {
                if (status === SUBMIT_STATUS.REJECTED) return;
                if (status === SUBMIT_STATUS.CREATED) {
                    trackChatEvent(CHAT_EVENTS.SEND_MESSAGE, {
                        sessionId: null,
                        isNewSession: true,
                        messageLength: pendingAutoRun.prompt.length,
                        usedVoice: pendingAutoRun.usedVoice,
                        is_top_nav: true,
                    });
                }
                setPendingAutoRun(current => (current === pendingAutoRun ? null : current));
            })
            .finally(() => {
                autoRunInFlightRef.current = false;
            });
    }, [pendingAutoRun, currentSessionId, loadingSessionId, chat, clearCurrentSession, setSessionParam]);

    const onShelfOpenChange = useCallback(isShelfOpen => {
        if (userControlsSidebarRef.current) return;
        setShowSessionSidebar(!isShelfOpen);
    }, []);

    const hasMessages = messages.length > 0;

    if (authLoading) {
        return (
            <div className={css.chatView}>
                <div className={css.loading}>
                    <Icon name="loader" size={IconSizes.LARGE} className={css.spinningLoader} />
                    <div className={css.loadingText}>
                        <Translate id={`${TRANSLATION_PREFIX}.AUTH_LOADING`} />
                    </div>
                </div>
            </div>
        );
    }

    if (authError) {
        return (
            <div className={css.chatView}>
                <div className={css.authError}>
                    <Icon name="alert-triangle" size={IconSizes.LARGE} className={css.authErrorIcon} />
                    <div className={css.authErrorText}>
                        <Translate id={`${TRANSLATION_PREFIX}.AUTH_ERROR`} />
                    </div>
                </div>
            </div>
        );
    }

    const onNewChat = () => {
        trackChatEvent(CHAT_EVENTS.NEW_CHAT, { previousSessionId: currentSessionId });
        clearCurrentSession();
    };

    const toggleSessionSidebar = () => {
        trackChatEvent(CHAT_EVENTS.TOGGLE_SESSION_SIDEBAR, { isOpen: !showSessionSidebar });
        userControlsSidebarRef.current = true;
        setShowSessionSidebar(!showSessionSidebar);
    };

    const onSessionSelect = async sessionId => {
        if (loadingSessionId) return;
        if (sessionId !== currentSessionId) {
            trackChatEvent(CHAT_EVENTS.OPEN_CHAT, { sessionId });
            await loadSession(sessionId);
        }
    };

    const onSessionUpdate = async (sessionId, updates) => {
        if (updates.title) return await updateSessionTitle(sessionId, updates.title);
        return true;
    };

    return (
        <ConnectionsProvider>
            <SlackAgentProvider>
                <div className={css.chatView}>
                    {streamDescriptors.map(({ key, runId, resumeSignal }) => (
                        <StreamMonitor
                            key={runId}
                            sessionKey={key}
                            runId={runId}
                            onMessage={handleStreamMessage}
                            onError={handleStreamError}
                            resumeSignal={resumeSignal}
                        />
                    ))}
                    <SubPageHeader
                        labelTranslationKey={`${TRANSLATION_PREFIX}.TITLE`}
                        className={css.chatHeader}
                        rightSection={
                            <div className={css.headerActions}>
                                <ChatConnections />
                                {isAdminMode && <ContextEnvSelect selectedEnv={contextEnv} onChange={setContextEnv} />}
                                {isAdminMode && (
                                    <Icon
                                        name="bug"
                                        size={IconSizes.MEDIUM}
                                        variant={showSessionDebug ? IconVariants.ACTIVE : IconVariants.DARK}
                                        onClick={() => setShowSessionDebug(open => !open)}
                                        hoverText={`${TRANSLATION_PREFIX}.SESSION_DEBUG_HOVER`}
                                    />
                                )}
                                {hasMessages && (
                                    <Button Icon="plus" onClick={onNewChat}>
                                        <Translate id={`${TRANSLATION_PREFIX}.NEW_CHAT`} />
                                    </Button>
                                )}
                            </div>
                        }
                    />
                    <div className={css.body}>
                        <SessionNoticeToasts
                            chat={chat}
                            onSessionGone={sessionId => {
                                if (sessionId === urlSessionId) setSessionParam(null);
                            }}
                        />
                        <div className={classNames(css.sessionSidebar, { [css.collapsed]: !showSessionSidebar })}>
                            <SessionList
                                sessions={sessions}
                                currentSessionId={currentSessionId}
                                loadingSessionId={loadingSessionId}
                                streamingSessionIds={streamingSessionIds}
                                updatedSessionIds={updatedSessionIds}
                                onSessionSelect={onSessionSelect}
                                onSessionDelete={deleteSession}
                                onSessionUpdate={onSessionUpdate}
                                isLoading={isLoadingSessions}
                                loadError={sessionsLoadError}
                                onRetry={fetchSessions}
                            />
                        </div>
                        <Icon
                            name={showSessionSidebar ? 'chevron-left' : 'chevron-right'}
                            size={IconSizes.MEDIUM}
                            className={classNames(css.sidebarButton, { [css.collapsed]: !showSessionSidebar })}
                            onClick={toggleSessionSidebar}
                            hoverText={`${TRANSLATION_PREFIX}.${
                                showSessionSidebar ? 'HIDE_SIDEBAR_HOVER' : 'SHOW_SIDEBAR_HOVER'
                            }`}
                        />
                        <ChatConversation
                            chat={chat}
                            showEmptyState
                            showSessionDebug={showSessionDebug}
                            onSessionDebugClose={() => setShowSessionDebug(false)}
                            onShelfOpenChange={onShelfOpenChange}
                        />
                    </div>
                </div>
            </SlackAgentProvider>
        </ConnectionsProvider>
    );
};

export default AnalysisChat;
